import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Icon } from 'semantic-ui-react';
import moment from 'moment-timezone';
import ptBr from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';

import Popover from '../Popover';
import {
  CalendarWrapper,
  DateInput,
  DateInputGroup,
  DateInputLabel,
  DateInputsRow,
  DateRangeFooter,
  DateRangeLayout,
  DateRangeMain,
  DateRangeTrigger,
  DateRangeValue,
  PresetItem,
  PresetList,
  PrimaryButton,
  RelativeWrap,
  TextButton
} from '../styles';
import { DateRangeFilterField, DateRangePresetKey } from '../types';
import {
  DATE_RANGE_PRESETS,
  detectPreset,
  formatDateBR,
  formatDateRangeLabel,
  getPresetByKey,
  parseDateBR
} from './dateRangePresets';

registerLocale('ptBr', ptBr);

export type DateRangeFieldProps = {
  field: DateRangeFilterField;
  startValue?: Date | string | null;
  endValue?: Date | string | null;
  onChange: (start: Date | null, end: Date | null) => void;
  disabled?: boolean;
};

const toDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  field,
  startValue,
  endValue,
  onChange,
  disabled
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const appliedStart = toDate(startValue);
  const appliedEnd = toDate(endValue);

  const [draftStart, setDraftStart] = useState<Date | null>(appliedStart);
  const [draftEnd, setDraftEnd] = useState<Date | null>(appliedEnd);
  const [activeInput, setActiveInput] = useState<'start' | 'end'>('start');
  const [startText, setStartText] = useState(formatDateBR(appliedStart));
  const [endText, setEndText] = useState(formatDateBR(appliedEnd));
  const [selectedPreset, setSelectedPreset] = useState<DateRangePresetKey>(
    detectPreset(appliedStart, appliedEnd)
  );

  const availablePresets = field.presets
    ? DATE_RANGE_PRESETS.filter(preset => field.presets!.includes(preset.key))
    : DATE_RANGE_PRESETS;

  useEffect(() => {
    if (!open) return;
    setDraftStart(appliedStart);
    setDraftEnd(appliedEnd);
    setStartText(formatDateBR(appliedStart));
    setEndText(formatDateBR(appliedEnd));
    setSelectedPreset(detectPreset(appliedStart, appliedEnd));
    setActiveInput('start');
  }, [open, appliedStart, appliedEnd]);

  const handlePresetClick = (key: DateRangePresetKey) => {
    setSelectedPreset(key);
    if (key === 'custom') {
      setActiveInput('start');
      return;
    }
    const preset = getPresetByKey(key);
    if (!preset) return;
    const range = preset.getRange();
    setDraftStart(range.start);
    setDraftEnd(range.end);
    setStartText(formatDateBR(range.start));
    setEndText(formatDateBR(range.end));
  };

  const handleCalendarChange = (dates: [Date | null, Date | null] | Date | null) => {
    setSelectedPreset('custom');

    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setDraftStart(start);
      setDraftEnd(end);
      setStartText(formatDateBR(start));
      setEndText(formatDateBR(end));
      if (start && !end) setActiveInput('end');
      if (start && end) setActiveInput('start');
      return;
    }

    if (activeInput === 'start') {
      setDraftStart(dates);
      setStartText(formatDateBR(dates));
      if (dates && draftEnd && moment(dates).isAfter(draftEnd)) {
        setDraftEnd(null);
        setEndText('');
      }
      setActiveInput('end');
    } else {
      setDraftEnd(dates);
      setEndText(formatDateBR(dates));
    }
  };

  const handleStartTextBlur = () => {
    const parsed = parseDateBR(startText);
    if (parsed) {
      setDraftStart(parsed);
      setSelectedPreset('custom');
    } else if (!startText) {
      setDraftStart(null);
    } else {
      setStartText(formatDateBR(draftStart));
    }
  };

  const handleEndTextBlur = () => {
    const parsed = parseDateBR(endText);
    if (parsed) {
      setDraftEnd(parsed);
      setSelectedPreset('custom');
    } else if (!endText) {
      setDraftEnd(null);
    } else {
      setEndText(formatDateBR(draftEnd));
    }
  };

  const handleApply = () => {
    onChange(draftStart, draftEnd);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const displayValue = formatDateRangeLabel(appliedStart, appliedEnd);

  return (
    <RelativeWrap>
      <DateRangeTrigger
        ref={anchorRef}
        type="button"
        open={open}
        hasValue={Boolean(displayValue)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => !disabled && setOpen(prev => !prev)}
      >
        <Icon name="calendar alternate outline" />
        <DateRangeValue>
          {displayValue || field.placeholder || 'Selecione'}
        </DateRangeValue>
      </DateRangeTrigger>

      <Popover
        open={open}
        onClose={handleCancel}
        anchorRef={anchorRef}
        width={560}
        align="left"
      >
        <DateRangeLayout>
          <PresetList>
            {availablePresets.map(preset => (
              <PresetItem
                key={preset.key}
                type="button"
                selected={selectedPreset === preset.key}
                onClick={() => handlePresetClick(preset.key)}
              >
                {preset.label}
              </PresetItem>
            ))}
          </PresetList>

          <DateRangeMain>
            <DateInputsRow>
              <DateInputGroup>
                <DateInputLabel>Data de Início</DateInputLabel>
                <DateInput
                  active={activeInput === 'start'}
                  value={startText}
                  placeholder="dd/mm/aaaa"
                  onFocus={() => setActiveInput('start')}
                  onChange={e => setStartText(e.target.value)}
                  onBlur={handleStartTextBlur}
                />
              </DateInputGroup>
              <DateInputGroup>
                <DateInputLabel>Data de Término</DateInputLabel>
                <DateInput
                  active={activeInput === 'end'}
                  value={endText}
                  placeholder="dd/mm/aaaa"
                  onFocus={() => setActiveInput('end')}
                  onChange={e => setEndText(e.target.value)}
                  onBlur={handleEndTextBlur}
                />
              </DateInputGroup>
            </DateInputsRow>

            <CalendarWrapper>
              <DatePicker
                selected={draftStart}
                onChange={handleCalendarChange}
                startDate={draftStart}
                endDate={draftEnd}
                selectsRange
                inline
                locale="ptBr"
                monthsShown={1}
                dateFormat="dd/MM/yyyy"
                calendarStartDay={0}
              />
            </CalendarWrapper>

            <DateRangeFooter>
              <TextButton type="button" onClick={handleCancel}>
                Cancelar
              </TextButton>
              <PrimaryButton type="button" onClick={handleApply}>
                Aplicar
              </PrimaryButton>
            </DateRangeFooter>
          </DateRangeMain>
        </DateRangeLayout>
      </Popover>
    </RelativeWrap>
  );
};

export default DateRangeField;
